/**
 * Global Logger Utility
 *
 * Uses vuejs-logger underneath with log4j-style levels:
 * - DEBUG: Detailed information for debugging
 * - INFO: General informational messages
 * - WARN: Warning messages for potentially harmful situations
 * - ERROR: Error messages for failures
 * - FATAL: Critical errors that might cause application to abort
 *
 * In production, only ERROR and FATAL are logged (configured in main.js)
 *
 * @see {@link ../../docs/development/logging.md} Full documentation and examples
 *
 * @example Basic usage
 *   import { createLogger } from '@/utils/logger'
 *   const logger = createLogger('auth')
 *   logger.debug('Token refreshed', { tokenData })
 *   logger.error('Request failed', error)
 */

import Vue from 'vue'

const isProduction = process.env.NODE_ENV === 'production'

/**
 * Creates a namespaced logger for a specific module
 * @param {string} namespace - Module name (e.g., 'auth', 'api', 'tasks')
 * @returns {Object} Logger instance with debug, info, warn, error, fatal methods
 */
export function createLogger(namespace) {
  const prefix = `[${namespace}]`

  return {
    /**
     * Debug level - detailed information for diagnosing problems
     * Only shown in development
     */
    debug: (...args) => {
      if (Vue.$log) {
        Vue.$log.debug(prefix, ...args)
      } else if (!isProduction) {
        console.log(prefix, '[DEBUG]', ...args)
      }
    },

    /**
     * Info level - informational messages highlighting application progress
     * Only shown in development
     */
    info: (...args) => {
      if (Vue.$log) {
        Vue.$log.info(prefix, ...args)
      } else if (!isProduction) {
        console.info(prefix, '[INFO]', ...args)
      }
    },

    /**
     * Warn level - potentially harmful situations
     * Only shown in development
     */
    warn: (...args) => {
      if (Vue.$log) {
        Vue.$log.warn(prefix, ...args)
      } else if (!isProduction) {
        console.warn(prefix, '[WARN]', ...args)
      }
    },

    /**
     * Error level - error events that might still allow the application to continue
     * Always logged, even in production
     */
    error: (...args) => {
      if (Vue.$log) {
        Vue.$log.error(prefix, ...args)
      } else {
        console.error(prefix, '[ERROR]', ...args)
      }
    },

    /**
     * Fatal level - very severe error events that will lead the application to abort
     * Always logged, even in production
     */
    fatal: (...args) => {
      if (Vue.$log) {
        Vue.$log.fatal(prefix, ...args)
      } else {
        console.error(prefix, '[FATAL]', ...args)
      }
    }
  }
}

// Default logger without namespace
const defaultLogger = createLogger('app')

export default defaultLogger
